<template>
    <div>
        <h1 class="display-heading">Vaccination Details</h1>

        <!-- Payment status -->
        <div class="card mb-3" v-if="!isUserBookedByCompany">
            <div class="card-header">Payment Status</div>
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col-md-3"><b>Status</b></div>
                    <div class="col-md-9">
                        <span class="badge text-nowrap" :class="{
                                    'badge-success': paymentStatus === 'Paid',
                                    'badge-secondary': !paymentStatus !== 'Paid',
                                }">
                            {{paymentStatus}}
                        </span>
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-3"><b>Price</b></div>
                    <div class="col-md-9">
                        RM{{ vaccination.pr ? vaccination.pr : '-' }}
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-3"><b>Payment Date</b></div>
                    <div class="col-md-9">
                        {{ vaccination.pd ? convertDateToDDMMYYYY(vaccination.pd) : '-' }}
                    </div>
                </div>
                <div class="row" v-if="paymentStatus === 'Not Paid' && (role === 1 || role === 4) && this.vaccination.aui === this.vaccination.bbi"> <!-- Only if user is USER or COMPANY role -->
                    <div class="col-md-3"><b>Action</b></div>
                    <div class="col-md-9">
                        <button class="btn btn-primary btn-primary-insv mr-2" @click="pay">Pay</button>
                    </div>
                </div>

                <!-- Payment Receipt -->
                <div v-if="paymentReceipts.issued.length>0 && isAdmin" class="row">
                    <div class="col-md-3"><b>{{paymentReceipts.issued.length>1?'Issued Payment Receipts' : 'Issued Payment Receipt'}}</b></div>
                    <div class="col-md-9">
                        <router-link v-for="(paymentReceipt, index) in paymentReceipts.issued" :key="index" :to="{'name': 'view-payment-receipt', params: {'id': paymentReceipt.paymentReceiptId}}" class="btn btn-primary btn-primary-insv mr-2 mb-2" target="_blank">
                            View Payment Receipt {{ paymentReceipts.issued.length > 1 ? index+1 : '' }}
                        </router-link>
                    </div>
                </div>
                <div v-if="paymentReceipts.received.length>0" class="row">
                    <div class="col-md-3"><b>{{paymentReceipts.received.length>1?'Payment Receipts' : 'Payment Receipt'}}</b></div>
                    <div class="col-md-9">
                        <router-link v-for="(paymentReceipt, index) in paymentReceipts.received" :key="index" :to="{'name': 'view-payment-receipt', params: {'id': paymentReceipt.paymentReceiptId}}" class="btn btn-primary btn-primary-insv mr-2 mb-2" target="_blank">
                            View Payment Receipt {{ paymentReceipts.received.length > 1 ? index+1 : '' }}
                        </router-link>
                    </div>
                </div>
                <!-- Payment Receipt -->

                <!-- Payment Slip -->
                <div v-if="paymentSlips.issued.length>0 && isAdmin" class="row">
                    <div class="col-md-3"><b>{{paymentSlips.issued.length>1?'Issued Payment Slips' : 'Issued Payment Slip'}}</b></div>
                    <div class="col-md-9">
                        <router-link v-for="(paymentSlip, index) in paymentSlips.issued" :key="index" :to="{'name': 'view-payment-slip', params: {'id': paymentSlip.paymentSlipId}}" class="btn btn-primary btn-primary-insv mr-2 mb-2" target="_blank">
                            View Payment Slip {{ paymentSlips.issued.length > 1 ? index+1 : '' }}
                        </router-link>
                    </div>
                </div>
                <div v-if="paymentSlips.received.length>0" class="row">
                    <div class="col-md-3"><b>{{paymentSlips.received.length>1?'Payment Slips' : 'Payment Slip'}}</b></div>
                    <div class="col-md-9">
                        <router-link v-for="(paymentSlip, index) in paymentSlips.received" :key="index" :to="{'name': 'view-payment-slip', params: {'id': paymentSlip.paymentSlipId}}" class="btn btn-primary btn-primary-insv mr-2 mb-2" target="_blank">
                            View Payment Slip {{ paymentSlips.received.length > 1 ? index+1 : '' }}
                        </router-link>
                    </div>
                </div>
                <!-- Payment Slip -->

                <!-- Invoice -->
                <div v-if="invoices.issued.length>0 && isAdmin" class="row">
                    <div class="col-md-3"><b>{{invoices.issued.length>1?'Issued Invoices' : 'Issued Invoice'}}</b></div>
                    <div class="col-md-9">
                        <router-link v-for="(invoice, index) in invoices.issued" :key="index" :to="{'name': 'view-invoice', params: {'id': invoice.invoiceId}}" class="btn btn-primary btn-primary-insv mr-2 mb-2" target="_blank">
                            View Invoice {{ invoices.issued.length > 1 ? index+1 : '' }}
                        </router-link>
                    </div>
                </div>
                <div v-if="invoices.received.length>0" class="row">
                    <div class="col-md-3"><b>{{invoices.received.length>1?'Invoices' : 'Invoice'}}</b></div>
                    <div class="col-md-9">
                        <router-link v-for="(invoice, index) in invoices.received" :key="index" :to="{'name': 'view-invoice', params: {'id': invoice.invoiceId}}" class="btn btn-primary btn-primary-insv mr-2 mb-2" target="_blank">
                            View Invoice {{ invoices.received.length > 1 ? index+1 : '' }}
                        </router-link>
                    </div>
                </div>
                <!-- Invoice -->
            </div>
        </div>

        <!-- Supplier Details -->
        <div class="card mb-3" v-if="isAdmin">
            <div class="card-header">Supplier Details</div>
            <div class="card-body">
                 <div class="row mb-3" v-if="vaccination.vs">
                    <div class="col-md-3"><b>Supplier</b></div>
                    <div class="col-md-9">
                        {{ vaccination.vs }}
                    </div>
                </div>
                <div class="row" v-if="isAdmin && vaccination.vs === null">
                    <div class="col-md-3"><b>Action</b></div>
                    <div class="col-md-9 form-inline">
                        <!-- DROPDOWN -->
                        <select class="form-control mr-3" v-model="vaccinationSupplier">
                            <option value="0" disabled>Please select vaccine supplier</option>
                            <option v-for="vaccinationSupplier in vaccinationSuppliers" :key="vaccinationSupplier.id" :value="vaccinationSupplier.id">{{ vaccinationSupplier.name }}</option>
                        </select>
                        <button @click="$refs.assignVaccinationSupplierPopup.show()" class="btn btn-primary btn-primary-insv mr-2" :disabled="vaccinationSupplier === '0'">Assign Vaccination Supplier</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Location Details -->
        <div class="card mb-3">
            <div class="card-header">Location Details</div>
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col-md-3"><b>Location Type</b></div>
                    <div class="col-md-9">
                        {{ vaccination.lt }}
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-3"><b>Assigned Clinic</b></div>
                    <div class="col-md-9">
                        {{ vaccination.vc ? vaccination.vc : '-' }}
                    </div>
                </div>
                <div class="row mb-3">
                        <div class="col-md-3"><b>{{ vaccination.lt === 'Clinic' ? 'Clinic Address' : 'Onsite Address' }}</b></div>
                        <div class="col-md-9" v-if="vaccination.lt === 'Clinic'">
                            {{ vaccination.a1 ? `${(vaccination.a2 ? vaccination.a1 + ', ' + vaccination.a2 : vaccination.a1)}, ${vaccination.c}, ${vaccination.p} ${vaccination.s}` : '-' }}
                        </div>
                        <div class="col-md-9" v-else>
                            {{ `${(vaccination.a2 ? vaccination.a1 + ', ' + vaccination.a2 : vaccination.a1)}, ${vaccination.c}, ${vaccination.p} ${vaccination.s}` }}
                        </div>
                    </div>
                <template v-if="vaccination.lt === 'Clinic'">
                    <div class="row mb-3"  v-if="isAdmin">
                        <div class="col-md-3"><b>Customer Address</b></div>
                        <div class="col-md-9">
                            {{ vaccination.ua1 ? `${(vaccination.ua2 ? vaccination.ua1 + ', ' + vaccination.ua2 : vaccination.ua1)}, ${vaccination.uc}, ${vaccination.up} ${vaccination.us}` : '-' }}
                        </div>
                    </div>
                    <div class="row mb-3">
                        <div class="col-md-3"><b>Region</b></div>
                        <div class="col-md-9">
                            {{ vaccination.r }}
                        </div>
                    </div>
                </template>
                <template v-if="vaccination.lt === 'Onsite' && (vaccination.aui === vaccination.bbi || isAdmin)">
                    <div class="row mb-3">
                        <div class="col-md-3"><b>Person in Charge</b></div>
                        <div class="col-md-9">
                            {{ vaccination.pn }}
                        </div>
                    </div>
                    <div class="row mb-3">
                        <div class="col-md-3"><b>Person in Charge Phone</b></div>
                        <div class="col-md-9">
                            {{ vaccination.pp }}
                        </div>
                    </div>
                    <div class="row mb-3">
                        <div class="col-md-3"><b>Prefered Date 1</b></div>
                        <div class="col-md-9">
                            {{ convertDateToDDMMYYYY(vaccination.pd1) }}
                        </div>
                    </div>
                    <div class="row mb-3">
                        <div class="col-md-3"><b>Prefered Date 2</b></div>
                        <div class="col-md-9">
                            {{ convertDateToDDMMYYYY(vaccination.pd2) }}
                        </div>
                    </div>
                </template>

                <template v-if="isAdmin && vaccination.st === 'Paid' && vaccination.vc === null">
                    <div class="row mb-3">
                        <div class="col-md-3"><b>Patient Count</b></div>
                        <div class="col-md-9">{{ vaccination.vp.length }}</div>
                    </div>
                    <div class="row">
                        <div class="col-md-3"><b>Action</b></div>
                        <div class="col-md-9 form-inline">
                            <!-- DROPDOWN -->
                            <select class="form-control mr-3" v-model="vaccinationCentre">
                                <option value="1" disabled>Please select clinic</option>
                                <option v-for="vaccinationCentre in vaccinationCentres" :key="vaccinationCentre.i" :value="vaccinationCentre.i">[{{ vaccinationCentre.rn }}] - {{ vaccinationCentre.n }} ({{vaccinationCentre.avc}})</option>
                            </select>
                            <button @click="$refs.assignVaccinationCentrePopup.show()" class="btn btn-primary btn-primary-insv mr-2" :disabled="vaccinationCentre === '1'">Assign Clinic</button>
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <!-- Clinic Payment status -->
        <div class="card mb-3" v-if="isAdmin"> <!-- Only if user is SUPERADMIN role -->
            <div class="card-header">Clinic Payment Status</div>
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col-md-3"><b>Status</b></div>
                    <div class="col-md-9">
                        <span class="badge text-nowrap" :class="{
                                    'badge-success': vaccination.ppd !== null,
                                    'badge-secondary': vaccination.ppd === null ,
                                }">
                            {{vaccination.ppd ? 'Paid' : 'Not Paid'}}
                        </span>
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-3"><b>Bank Name</b></div>
                    <div class="col-md-9">
                        {{ vaccination.bn ? vaccination.bn : '-' }}
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-3"><b>Bank Account Payee Name</b></div>
                    <div class="col-md-9">
                        {{ vaccination.bapn ? vaccination.bapn : '-' }}
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-3"><b>Bank Account Number</b></div>
                    <div class="col-md-9">
                        {{ vaccination.ban ? vaccination.ban : '-' }}
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-3"><b>Clinic Payment Date</b></div>
                    <div class="col-md-9">
                        {{ vaccination.ppd ? convertDateToDDMMYYYY(vaccination.ppd) : '-' }}
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-3"><b>Clinic Payment Amount</b></div>
                    <div class="col-md-9">
                        RM {{ vaccination.ppa ? vaccination.ppa : '-' }}
                    </div>
                </div>
                <div class="row" v-if="vaccination.st === 'Assigned to Clinic' && vaccination.ppd === null">
                    <div class="col-md-3"><b>Action</b></div>
                    <div class="col-md-9">
                        <button @click="$refs.confirmPartnerPaymentPopup.show()" class="btn btn-primary btn-primary-insv mr-2">Mark as Paid</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Dosage Info -->
        <div v-if="(vaccination.vp && vaccination.ut === 'User' || isUserBookedByCompany)">
            <table class="table table-bordered table-sm">
                <colgroup>
                    <col width="250">
                    <col>
                </colgroup>
                <tr>
                    <th colspan="2" class="text-uppercase">Dose Info</th>
                </tr>
                <template v-for="dose in vaccination.vp[0].d">
                    <tr :key="dose.di">
                        <th>Dose {{ dose.d }}</th>
                        <td class="form-inline" v-if="role === 10 && dose.da === null"> <!-- Only if user i VACCINATION CLINIC role -->
                            <input type="text" :name="'dose' + dose.d" :data-vv-as="'dose ' + dose.d" data-vv-validate-on="blur"
                                v-model="doses[dose.d]"
                                v-mask="'##/##/####'"
                                v-validate="'required|date_format:DD/MM/YYYY'" placeholder="DD/MM/YYYY"
                                :class="{
                                'form-control': true,
                                'is-invalid':  validationErrors.has('dose' + dose.d)
                            }" />
                            <button @click="validateDoseDate(dose.d)" class="btn btn-primary btn-primary-insv ml-3">Set Date</button>
                        </td>
                        <td v-else>{{ dose.da ? convertDateToDDMMYYYY(dose.da) : '-' }}</td>
                    </tr>
                </template>
            </table>
        </div>

        <!-- Vaccination Info -->
        <div>
            <table class="table table-bordered table-sm">
                <colgroup>
                    <col width="250">
                    <col>
                </colgroup>
                <tr>
                    <th colspan="2" class="text-uppercase">Vaccination Info</th>
                </tr>
                <tr>
                    <th>ID</th>
                    <td>{{vaccination.vis}}</td>
                </tr>
                <tr v-if="isAdmin">
                    <th>User Type</th>
                    <td>{{vaccination.ut}}</td>
                </tr>
                <tr>
                    <th>Vaccine</th>
                    <td>{{vaccination.v}}</td>
                </tr>
                <tr>
                    <th>Booking Date</th>
                    <td>{{vaccination.bd ? convertDateToDDMMYYYY(vaccination.bd) : 'Pending Payment'}}</td>
                </tr>
                <tr>
                    <th>Booking Made By</th>
                    <td>{{vaccination.bb}}</td>
                </tr>
                <tr>
                    <th>Status</th>
                    <td>{{vaccination.st}}</td>
                </tr>
            </table>

            <!-- Vaccination Patient -->
            <template v-if="vaccination.ut == 'User' || isUserBookedByCompany">
                <table class="table table-bordered table-sm">
                    <colgroup>
                        <col width="250">
                        <col>
                    </colgroup>
                    <tr>
                        <th colspan="2" class="text-uppercase">Patient Info</th>
                    </tr>
                    <tr>
                        <th>Name</th>
                        <td>{{vaccinationPatient.pn}}</td>
                    </tr>
                    <tr>
                        <th>Email</th>
                        <td>{{vaccinationPatient.pe}}</td>
                    </tr>
                    <tr>
                        <th>Phone</th>
                        <td>{{vaccinationPatient.pp}}</td>
                    </tr>
                    <tr>
                        <th>Identity Card Number</th>
                        <td>{{vaccinationPatient.picn}}</td>
                    </tr>
                    <tr>
                        <th>Date of Birth</th>
                        <td>{{convertDateToDDMMYYYY(vaccinationPatient.pdob)}}</td>
                    </tr>
                    <tr>
                        <th>Gender</th>
                        <td>{{vaccinationPatient.pg}}</td>
                    </tr>
                    <tr>
                        <th>Nationality</th>
                        <td>{{vaccinationPatient.pn}}</td>
                    </tr>
                    <tr>
                        <th>MySejahtera ID</th>
                        <td>{{vaccinationPatient.pmsi}}</td>
                    </tr>
                </table>
            </template>
            <template v-else>
                <table class="table table-bordered table-sm">
                    <colgroup>
                        <col width="250">
                        <col>
                    </colgroup>
                    <tr>
                        <th colspan="2" class="text-uppercase margin-auto">Patient Info</th>
                    </tr>
                    <tr>
                        <th>Patient Count</th>
                        <td>{{ vaccination.vp ? vaccination.vp.length : 0 }}</td>
                    </tr>
                    <tr v-if="role === 10"> <!-- Only if user i VACCINATION CLINIC role -->
                        <th>Action</th>
                        <td class="form-inline">
                            <button :disabled="vaccination.vp ? vaccination.vp.filter(vp => vp.isSelected).length <= 0 : false" @click="$refs.updateBulkDoseDatePopup.show()" class="btn btn-primary btn-primary-insv mr-3">Set Dose Date</button>
                            <div>
                                <div class="custom-control custom-checkbox custom-checkbox-red">
                                    <input type="checkbox" class="custom-control-input" id="indicator1" disabled>
                                    <label class="custom-control-label float-left" for="indicator1">Dose date applies to all vaccines</label>
                                </div>
                                <div class="custom-control custom-checkbox custom-checkbox-blue">
                                    <input type="checkbox" class="custom-control-input" id="indicator2" disabled>
                                    <label class="custom-control-label" for="indicator2">Dose date applies to selected vaccines</label>
                                </div>
                            </div>
                        </td>
                    </tr>
                </table>
                <table class="table" style="font-size: 14px;">
                    <col width="100">
                    <col width="100">
                    <col width="100">
                    <col width="100">
                    <col width="100">
                    <col width="100">
                    <col width="100">
                    <col width="100">
                    <col width="100">
                    <col width="100">
                    <thead>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">Name</th>
                            <th scope="col">Phone</th>
                            <th scope="col">Email</th>
                            <th scope="col">Identity Card Number</th>
                            <th scope="col">Date of Birth</th>
                            <th scope="col">Gender</th>
                            <th scope="col">Nationality</th>
                            <th scope="col">MySejahtera ID</th>
                            <th scope="col" v-if="role === 10"> <!-- Only if user i VACCINATION CLINIC role -->
                                <!-- <input type="checkbox" class="custom-control-input form-check-input m-auto" v-model="selectAllEmployeeBool" @click="selectAllEmployee"> -->
                                <div class="custom-control custom-checkbox custom-checkbox-red">
                                    <input type="checkbox" class="custom-control-input" id="selectAll" v-model="selectAllEmployeeBool" @click="selectAllEmployee">
                                    <label class="custom-control-label" for="selectAll"></label>
                                </div>
                            </th>
                            <!-- <th scope="col" v-if="(role === 4) && vaccination.aui === vaccination.bbi">
                                Action
                            </th> -->
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(employee, index) in vaccination.vp">
                            <tr :key="'details'+ index">
                                <td><i data-toggle="collapse" :data-target="'#collapse' + index" aria-expanded="true" :aria-controls="'collapse' + index" class="fas fa-chevron-down" style="cursor: pointer;"></i></td>
                                <td>{{ employee.pn }}</td>
                                <td>{{ employee.pp }}</td>
                                <td>{{ employee.pe }}</td>
                                <td>{{ employee.picn }}</td>
                                <td>{{ convertDateToDDMMYYYY(employee.pdob) }}</td>
                                <td>{{ employee.pg }}</td>
                                <td>{{ employee.pna }}</td>
                                <td>{{ employee.pmsi }}</td>
                                <td v-if="role === 10"> <!-- Only if user i VACCINATION CLINIC role -->
                                    <!-- <input type="checkbox" class="form-check-input m-auto" v-model="employee.isSelected"> -->

                                    <div class="custom-control custom-checkbox custom-checkbox-blue">
                                        <input type="checkbox" class="custom-control-input" :id="'employeeChecbox' + index" v-model="employee.isSelected">
                                        <label class="custom-control-label" :for="'employeeChecbox' + index"></label>
                                    </div>
                                </td>
                                <!-- <td v-if="(role === 4) && vaccination.aui === vaccination.bbi">
                                    <button :disabled="employee.d.find(dose => dose.da)" class="btn btn-primary btn-primary-insv" @click="$refs.popUpSwapEmployee.show(); pendingSwapIndex = index">Swap</button>
                                </td> -->
                            </tr>
                            <tr :key="'collapse'+ index" class="collapse show" :id="'collapse' + index">
                                <td colspan="9">
                                    <div v-if="vaccination.vp">
                                        <table class="table table-bordered table-sm">
                                            <colgroup>
                                                <col width="250">
                                                <col>
                                            </colgroup>
                                            <tr>
                                                <th colspan="2" class="text-uppercase">Dose Info</th>
                                            </tr>
                                            <template v-for="dose in employee.d">
                                                <tr :key="dose.di">
                                                    <th>Dose {{ dose.d }}</th>
                                                    <td>{{ dose.da ? convertDateToDDMMYYYY(dose.da) : '-' }}</td>
                                                </tr>
                                            </template>
                                        </table>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </template>

            <button class="btn btn-primary btn-primary-insv" @click="backToList">Back to List</button>
        </div>

        <div style="display: none;">
            <form ref="insvPayment" method="post" :action="paymentSubmitPath">
                <input type="text" name="UseWallet" value="false" />
                <input type="text" name="PaymentWalletAmount"  value="0" />
                <input type="text" name="PaymentSummaryId"  v-model="paymentSummaryId" />
                <input type="text" name="PaymentRemarks" value="Vaccination" />
                <input name="Token" v-model="token" type="hidden">
            </form>
        </div>

        <popup-modal ref="popupPaymentStatus" :hasCloseButton="true" :title="paymentPrompt.paymentStatusTitle">
           <template slot="content">
               <p>{{paymentPrompt.paymentStatusDetails}}</p>
               <p v-if="paymentPrompt.line2">{{paymentPrompt.line2}}</p>
               <p v-if="paymentPrompt.line3">{{paymentPrompt.line3}}</p>
           </template>
           <template slot="footer">
               <button @click="dismissPaymentStatus" class="btn btn-primary btn-primary-insv">Okay</button>
           </template>
        </popup-modal>

        <popup-modal ref="assignVaccinationSupplierPopup" :hasCloseButton="false" title="Vaccination Supplier Assignment Confirmation">
           <template slot="content">
                <p v-if="!isUpdating">Confirm assign supplier <b>{{ vaccinationSuppliers.find(vs => vs.id === vaccinationSupplier) ? vaccinationSuppliers.find(vs => vs.id === vaccinationSupplier).name  : '' }}</b> to vaccination?</p>
                <p v-else>Updating... Please wait.</p>
           </template>
           <template slot="footer">
               <button :disabled="isUpdating" @click="$refs.assignVaccinationSupplierPopup.hide()" class="btn btn-danger">No</button>
               <button :disabled="isUpdating" @click="updateVaccinationSupplier" class="btn btn-primary btn-primary-insv">Yes</button>
           </template>
        </popup-modal>

        <popup-modal ref="assignVaccinationCentrePopup" :hasCloseButton="false" title="Clinic Assignment Confirmation">
           <template slot="content">
                <p v-if="!isUpdating">Confirm assign vaccination to <b>{{ vaccinationCentres.find(vc => vc.i === vaccinationCentre) ? vaccinationCentres.find(vc => vc.i === vaccinationCentre).n  : '' }}</b>?</p>
                <p v-else>Updating... Please wait.</p>
           </template>
           <template slot="footer">
               <button :disabled="isUpdating" @click="$refs.assignVaccinationCentrePopup.hide()" class="btn btn-danger">No</button>
               <button :disabled="isUpdating" @click="updateVaccinationCentre" class="btn btn-primary btn-primary-insv">Yes</button>
           </template>
        </popup-modal>

        <popup-modal ref="confirmPartnerPaymentPopup" :hasCloseButton="false" title="Confirm Clinic Payment">
           <template slot="content">
                <p v-if="!isUpdating">Confirm payment to clinic? After this the payment will be mark as completed.</p>
                <p v-else>Updating... Please wait.</p>
           </template>
           <template slot="footer">
               <button :disabled="isUpdating" @click="$refs.confirmPartnerPaymentPopup.hide()" class="btn btn-danger">No</button>
               <button :disabled="isUpdating" @click="payVaccinationCentre" class="btn btn-primary btn-primary-insv">Yes</button>
           </template>
        </popup-modal>

        <popup-modal ref="updateDoseDatePopup" :hasCloseButton="false" title="Confirm Dose Date">
           <template slot="content">
                <p v-if="!isUpdating">Confirm set dose <b>{{ dose }}</b> to <b>{{ doses[dose] }}?</b></p>
                <p v-else>Updating... Please wait.</p>
           </template>
           <template slot="footer">
               <button :disabled="isUpdating" @click="$refs.updateDoseDatePopup.hide()" class="btn btn-danger">No</button>
               <button :disabled="isUpdating" @click="updateDoseDate" class="btn btn-primary btn-primary-insv">Yes</button>
           </template>
        </popup-modal>

        <popup-modal ref="updateBulkDoseDatePopup" :hasCloseButton="false" title="Confirm Bulk Dose Date">
           <template v-if="vaccination.vp" slot="content">
               <div class="mb-2">
                   <label for="">Dose:</label>
                   <select v-model="bulkDose" class="form-control">
                       <option v-for="(dose, index) in vaccination.vp[0].d" :key="'bulkDose' + index" :value="dose.d">{{ dose.d }}</option>
                   </select>
               </div>
                <div class="">
                    <label for="">Date:</label>
                    <input type="text" name="bulkDoseDate" :data-vv-as="'dose date'" data-vv-validate-on="blur"
                            v-model="bulkDoseDate"
                            v-mask="'##/##/####'"
                            v-validate="'required|date_format:DD/MM/YYYY'" placeholder="DD/MM/YYYY"
                            :class="{
                            'form-control': true,
                            'is-invalid':  validationErrors.has('bulkDoseDate')
                        }" />
                </div>
                <p class="mt-2 mb-2" v-if="isUpdating">Updating... Please wait.</p>
           </template>
           <template slot="footer">
               <button :disabled="isUpdating" @click="$refs.updateBulkDoseDatePopup.hide()" class="btn btn-danger">Close</button>
               <button :disabled="isUpdating" @click="validateBulkDoseDate" class="btn btn-primary btn-primary-insv">Update</button>
           </template>
        </popup-modal>

        <popup-modal ref="popUpSwapEmployee" title="Swap Employee" :hasCloseButton="true" sizeClass="modal-xl">
            <template v-if="!swapConfirmation">
                <template slot="content">
                    <p><b>Please select employee type.</b></p>
                    <div class="mb-3">
                        <div class="form-check form-check-inline">
                            <input class="form-check-input " type="radio" name="registeredEmployee" id="registeredEmployee" value="1" v-model="employeeType">
                            <label class="form-check-label" for="registeredEmployee">
                                Registered Employee
                            </label>
                        </div>
                        <div class="form-check form-check-inline">
                            <input class="form-check-input" type="radio" name="unregisteredEmployee" id="unregisteredEmployee" value="2" v-model="employeeType">
                            <label class="form-check-label" for="unregisteredEmployee">
                                Unregistered Employee
                            </label>
                        </div>
                    </div>

                    <template v-if="employeeType === '1'">
                        <div class="row mb-3">
                            <div class="col-lg-3 col-12">
                                <label class="mr-2 font-weight-bold">Full Name:</label>
                                <input type="text" name="filterName" data-vv-as="filter full name" data-vv-validate-on="blur"
                                    v-model="filterName"
                                    :class="{
                                        'form-control': true,
                                        'is-invalid':  validationErrors.has('filterName')
                                    }">
                                <div v-show="validationErrors.has('filterName')" class="invalid-feedback">
                                    {{ validationErrors.first('filterName') }}
                                </div>
                            </div>
                            <div class="col-lg-3 col-12">
                                <label class="mr-2 font-weight-bold">Identity Type:</label>
                                <select name="filterIdentityType" data-vv-validate-on="blur" data-vv-as="identification type"
                                    v-validate="identificationTypeValidation" v-model="filterIdentityType" :class="{
                                    'form-control': true,
                                    'is-invalid':  validationErrors.has('filterIdentityType')
                                }">
                                    <option value="0">Please Select</option>
                                    <option v-for="(identificationType, index) in identificationTypes" :key="index"
                                        :value="identificationType.value">
                                        {{identificationType.text}}
                                    </option>
                                </select>
                                <div v-show="validationErrors.has('filterIdentityType')" class="invalid-feedback">
                                    {{ validationErrors.first('filterIdentityType') }}
                                </div>
                            </div>
                            <div class="col-lg-3 col-12">
                                <label class="mr-2 font-weight-bold">Identity Number:</label>
                                <input type="text" name="filterIdentityNumber" data-vv-as="filter NRIC" data-vv-validate-on="blur"
                                    v-mask="filterIdentityType === 1 ? '######-##-####' : null" v-model="filterIdentityNumber"
                                    :class="{
                                        'form-control': true,
                                        'is-invalid':  validationErrors.has('filterIdentityNumber')
                                    }">
                                <div v-show="validationErrors.has('filterIdentityNumber')" class="invalid-feedback">
                                    {{ validationErrors.first('filterIdentityNumber') }}
                                </div>
                            </div>
                            <div class="col-lg-3 col-12 d-flex align-items-end">
                                <button @click="getEmployees" class="btn btn-primary">Search</button>
                            </div>
                        </div>

                        <div class="table-responsive">
                            <div ref="tableSection">
                                <table ref="listTable" class="table table-hover">
                                    <thead>
                                        <tr>
                                            <th scope="col">#</th>
                                            <th scope="col">Employee Name</th>
                                            <th scope="col">NRIC</th>
                                            <th scope="col">Action</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr v-show="employeeList.length === 0">
                                            <td :colspan="columnsCount" class="text-center font-italic">
                                                {{isTableLoading ? "Loading Data": "No Data"}}
                                            </td>
                                        </tr>
                                        <tr v-for="(employee, index) in employeeList" :key="index">
                                            <th scope="row">{{((pagination.currentPage - 1) * pagination.pageSize ) + index + 1}}</th>
                                            <td>{{employee.name}}</td>
                                            <td>{{employee.nric}}</td>
                                            <td>
                                                <button class="btn btn-primary" :disabled="checkIfSelected(employee.userId)" @click="selectEmployee(employee)">Select</button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <pagination v-model="pagination.currentPage"
                                :totalPages="pagination.totalPages"
                                :scrollPosition="pagination.scrollPosition">
                            </pagination>
                        </div>
                    </template>

                    <template v-else-if="employeeType === '2'">
                        <p><b>Please enter unregistered employee details.</b></p>
                        <form ref="unregisteredEmployeeForm" data-vv-scope="unregisteredEmployee">
                            <div class="row">
                                <div class="col-lg-6 col-12 mb-2">
                                    <label class="mr-2">Name</label>
                                    <input type="text" name="name" data-vv-as="name" data-vv-validate-on="blur"
                                        v-model="unregisteredFields.name" v-validate="'required'"
                                        placeholder="Name"
                                        :class="{
                                            'form-control': true,
                                            'is-invalid':  validationErrors.has('unregisteredEmployee.name')
                                        }">
                                    <div v-show="validationErrors.has('unregisteredEmployee.name')" class="invalid-feedback">
                                        {{ validationErrors.first('unregisteredEmployee.name') }}
                                    </div>
                                </div>
                                <div class="col-lg-6 col-12 mb-2">
                                    <label class="mr-2">Phone</label>
                                    <input type="text" name="phone" data-vv-as="phone" data-vv-validate-on="blur"
                                        v-model="unregisteredFields.phone" v-validate="'required'"
                                        placeholder="E.g. 0123456789"
                                        :class="{
                                            'form-control': true,
                                            'is-invalid':  validationErrors.has('unregisteredEmployee.phone')
                                        }">
                                    <div v-show="validationErrors.has('unregisteredEmployee.phone')" class="invalid-feedback">
                                        {{ validationErrors.first('unregisteredEmployee.phone') }}
                                    </div>
                                </div>
                                <div class="col-lg-6 col-12 mb-2">
                                    <label class="mr-2">Email</label>
                                    <input type="text" name="email" data-vv-as="email" data-vv-validate-on="blur"
                                        v-model="unregisteredFields.email" v-validate="'required'"
                                        placeholder="Email"
                                        :class="{
                                            'form-control': true,
                                            'is-invalid':  validationErrors.has('unregisteredEmployee.email')
                                        }">
                                    <div v-show="validationErrors.has('unregisteredEmployee.email')" class="invalid-feedback">
                                        {{ validationErrors.first('unregisteredEmployee.email') }}
                                    </div>
                                </div>
                                <div class="col-lg-6 col-12 mb-2">
                                    <label class="mr-2">Identity Card Number / Passport Number / Others </label>
                                    <input type="text" name="identityCardNumber" data-vv-as="identity card number" data-vv-validate-on="blur"
                                        v-model="unregisteredFields.identityCardNumber" v-validate="'required'"
                                        placeholder="Identity Card Number"
                                        :class="{
                                            'form-control': true,
                                            'is-invalid':  validationErrors.has('unregisteredEmployee.identityCardNumber')
                                        }">
                                    <div v-show="validationErrors.has('unregisteredEmployee.identityCardNumber')" class="invalid-feedback">
                                        {{ validationErrors.first('unregisteredEmployee.identityCardNumber') }}
                                    </div>
                                </div>
                                <div class="col-lg-6 col-12 mb-2">
                                    <label class="mr-2">Date of Birth</label>
                                    <input type="text" name="dob" data-vv-as="birth date" data-vv-validate-on="blur"
                                            v-model="unregisteredFields.dob"
                                            v-mask="'##/##/####'"
                                            v-validate="'required|date_format:DD/MM/YYYY'" placeholder="DD/MM/YYYY"
                                            :class="{
                                            'form-control': true,
                                            'is-invalid':  validationErrors.has('unregisteredEmployee.dob')
                                        }" />
                                    <div v-show="validationErrors.has('unregisteredEmployee.dob')" class="invalid-feedback">
                                        {{ validationErrors.first('unregisteredEmployee.dob') }}
                                    </div>
                                </div>
                                <div class="col-lg-6 col-12 mb-2">
                                    <label class="mr-2">Gender</label>
                                    <select name="gender" data-vv-validate-on="blur" v-model="unregisteredFields.gender" v-validate="genderValidation"
                                        :class="{
                                        'form-control': true,
                                        'is-invalid':  validationErrors.has('unregisteredEmployee.gender')
                                    }">
                                        <option value="0">Please Select</option>
                                        <option v-for="(gender, index) in genders" :key="index" :value="gender.value">
                                            {{gender.text}}
                                        </option>
                                    </select>
                                    <div v-show="validationErrors.has('unregisteredEmployee.gender')" class="invalid-feedback">
                                        {{ validationErrors.first('unregisteredEmployee.gender') }}
                                    </div>
                                </div>
                                <div class="col-lg-6 col-12 mb-2">
                                    <label class="mr-2">Nationality</label>
                                    <input type="text" name="nationality" data-vv-as="nationality" data-vv-validate-on="blur"
                                        v-model="unregisteredFields.nationality" v-validate="'required'"
                                        placeholder="E.g. Malaysian"
                                        :class="{
                                            'form-control': true,
                                            'is-invalid':  validationErrors.has('unregisteredEmployee.nationality')
                                        }">
                                    <div v-show="validationErrors.has('unregisteredEmployee.nationality')" class="invalid-feedback">
                                        {{ validationErrors.first('unregisteredEmployee.nationality') }}
                                    </div>
                                </div>
                                <div class="col-lg-6 col-12 mb-2">
                                    <label class="mr-2">MySejahtera ID</label>
                                    <input type="text" name="mySejahteraId" data-vv-as="mySejahteraId" data-vv-validate-on="blur"
                                        v-model="unregisteredFields.mySejahteraId" v-validate="'required'"
                                        placeholder="E.g. 0123456789"
                                        :class="{
                                            'form-control': true,
                                            'is-invalid':  validationErrors.has('unregisteredEmployee.mySejahteraId')
                                        }">
                                    <div v-show="validationErrors.has('unregisteredEmployee.mySejahteraId')" class="invalid-feedback">
                                        {{ validationErrors.first('unregisteredEmployee.mySejahteraId') }}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </template>
                </template>

                <template slot="footer">
                    <template v-if="employeeType === '1'">
                        <p class="w-100 float-left"><b>Please enter additonal employee details.</b></p>
                        <div class="row w-100">
                            <div class="col-lg-6 col-12 mb-2">
                                <label for="">Name</label>
                                <input class="form-control" type="text" disabled v-model="registeredFields.name">
                            </div>
                            <div class="col-lg-6 col-12 mb-2">
                                <label for="">Identity Card Number</label>
                                <input class="form-control" type="text" disabled  v-model="registeredFields.identityCardNumber">
                            </div>
                            <div class="col-lg-6 col-12 mb-2">
                                <label class="mr-2">Nationality</label>
                                <input type="text" ref="nationality" name="nationality" data-vv-as="nationality" data-vv-validate-on="blur"
                                    v-model="registeredFields.nationality" v-validate="'required'"
                                    placeholder="E.g. Malaysian"
                                    :class="{
                                        'form-control': true,
                                        'is-invalid':  validationErrors.has('nationality')
                                    }">
                                <div v-show="validationErrors.has('nationality')" class="invalid-feedback">
                                    {{ validationErrors.first('nationality') }}
                                </div>
                            </div>
                            <div class="col-lg-6 col-12 mb-2">
                                <label class="mr-2">MySejahtera ID</label>
                                <input type="text" name="mySejahteraId" data-vv-as="mySejahteraId" data-vv-validate-on="blur"
                                    v-model="registeredFields.mySejahteraId" v-validate="'required'"
                                    placeholder="E.g. 0123456789"
                                    :class="{
                                        'form-control': true,
                                        'is-invalid':  validationErrors.has('mySejahteraId')
                                    }">
                                <div v-show="validationErrors.has('mySejahteraId')" class="invalid-feedback">
                                    {{ validationErrors.first('mySejahteraId') }}
                                </div>
                            </div>
                            <div class="col-12">
                                <div class="text-right">
                                    <button class="btn btn-secondary mr-2" @click="()=>{ $refs.popUpSwapEmployee.hide(); }">Cancel</button>
                                    <button class="btn btn-primary btn-primary-insv" @click="confirmSwap">Swap</button>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="employeeType === '2'">
                        <div class="text-right">
                            <button class="btn btn-secondary mr-2" @click="()=>{ $refs.popUpSwapEmployee.hide(); }">Cancel</button>
                            <button class="btn btn-primary btn-primary-insv" @click="confirmSwap">Swap</button>
                        </div>
                    </template>
                </template>
            </template>

            <template v-else>
                <template slot="content">
                    <p><b>Confirm Swap?</b></p>
                    <div class="row">
                        <div class="col-5">
                            <table class="table table-bordered table-sm">
                                <tr>
                                    <th>Name</th>
                                    <td>{{ vaccination.vp[pendingSwapIndex].pn }}</td>
                                </tr>
                                <tr>
                                    <th>Phone</th>
                                    <td>{{ vaccination.vp[pendingSwapIndex].pp }}</td>
                                </tr>
                                <tr>
                                    <th>Email</th>
                                    <td>{{ vaccination.vp[pendingSwapIndex].pe }}</td>
                                </tr>
                                <tr>
                                    <th>Identity Card Number</th>
                                    <td>{{ vaccination.vp[pendingSwapIndex].picn }}</td>
                                </tr>
                                <tr>
                                    <th>Date of Birth</th>
                                    <td>{{ convertDateToDDMMYYYY(vaccination.vp[pendingSwapIndex].pdob) }}</td>
                                </tr>
                                <tr>
                                    <th>Gender</th>
                                    <td>{{ vaccination.vp[pendingSwapIndex].pg }}</td>
                                </tr>
                                <tr>
                                    <th>Nationality</th>
                                    <td>{{ vaccination.vp[pendingSwapIndex].pna }}</td>
                                </tr>
                                <tr>
                                    <th>MySejahtera ID</th>
                                    <td>{{ vaccination.vp[pendingSwapIndex].pmsi }}</td>
                                </tr>
                            </table>
                            <p :class="{ 'text-danger': swapError }">{{ swapMessage }}</p>
                        </div>
                        <div class="col-2"><b><i style="position: absolute; top: 50%; right: 50%;" class="fas fa-chevron-right"></i></b></div>
                        <div class="col-5">
                            <table class="table table-bordered table-sm">
                                <tr>
                                    <th>Name</th>
                                    <td>{{ employeeType === '1' ? registeredFields.name : unregisteredFields.name }}</td>
                                </tr>
                                <tr>
                                    <th>Phone</th>
                                    <td>{{ employeeType === '1' ? registeredFields.phone : unregisteredFields.phone }}</td>
                                </tr>
                                <tr>
                                    <th>Email</th>
                                    <td>{{ employeeType === '1' ? registeredFields.email : unregisteredFields.email }}</td>
                                </tr>
                                <tr>
                                    <th>Identity Card Number</th>
                                    <td>{{ employeeType === '1' ? registeredFields.identityCardNumber : unregisteredFields.identityCardNumber }}</td>
                                </tr>
                                <tr>
                                    <th>Date of Birth</th>
                                    <td>{{ employeeType === '1' ? convertDateToDDMMYYYY(registeredFields.dob) : unregisteredFields.dob }}</td>
                                </tr>
                                <tr>
                                    <th>Gender</th>
                                    <td>{{ employeeType === '1' ? registeredFields.gender === 1 ? 'Male' : 'Female' : unregisteredFields.gender === 1 ? 'Male' : 'Female' }}</td>
                                </tr>
                                <tr>
                                    <th>Nationality</th>
                                    <td>{{ employeeType === '1' ? registeredFields.nationality : unregisteredFields.nationality }}</td>
                                </tr>
                                <tr>
                                    <th>MySejahtera ID</th>
                                    <td>{{ employeeType === '1' ? registeredFields.mySejahteraId : unregisteredFields.mySejahteraId }}</td>
                                </tr>
                            </table>
                        </div>
                    </div>
                </template>
                <template slot="footer">
                    <button :disabled="isUpdating" class="btn btn-secondary" @click="swapConfirmation = false;">Cancel</button>
                    <button :disabled="isUpdating" class="btn btn-primary btn-primary-insv" @click="swap">Confirm</button>
                </template>
            </template>
        </popup-modal>

    </div>
</template>

<script>
import { apiPath } from '@/config';
import { convertDateToDDMMYYYY, convertDateToIso8601 } from '@/utilities';
import popupModal from '@/components/popup-modal';
import pagination from '@/components/pagination';
import identificationTypes from '@/config/identificationTypes';
import { VueMaskDirective } from 'v-mask';
import Cookies from 'js-cookie';
import genders from '@/config/genders';
export default {
    'name': 'VaccinationDetails',
    'directives': {
        'mask': VueMaskDirective
    },
    'components': {
        popupModal,
        pagination
    },
    'computed': {
        vaccinationId () {
            return this.$route.params.id;
        },
        isAdmin () {
            return this.role === 8;
        },
        paymentStatus () {
            if (this.vaccination) {
                if (this.vaccination.st === 'Not Paid' || this.vaccination.st === 'Payment Pending') {
                    return this.vaccination.st;
                } else {
                    return 'Paid';
                }
            }
            return false;
        },
        paymentSubmitPath () {
            return `${apiPath}MakePayment/Pay`;
        },
        isUserBookedByCompany () {
            return this.role === 1 && this.vaccination.aui !== this.vaccination.bbi;
        },
        token () {
            return Cookies.get('t');
        },
        currentPage () {
            return this.pagination.currentPage;
        },
        identificationTypeValidation () {
            let minIdentificationType = this.identificationTypes[0].value;
            let maxIdentificationType = this.identificationTypes[this.identificationTypes.length - 1].value;

            return `required|between:${minIdentificationType},${maxIdentificationType}`;
        },
        genderValidation () {
            let minGenderValue = this.genders[0].value;
            let maxGenderValue = this.genders[this.genders.length - 1].value;

            return `required|between:${minGenderValue},${maxGenderValue}`;
        }
    },
    data () {
        return {
            convertDateToDDMMYYYY,
            identificationTypes,
            genders,
            'isUpdating': false,
            'role': 0,
            'vaccination': {},
            'vaccinationPatient': {},
            'vaccinationCentres': [],
            'vaccinationSuppliers': [],
            'doses': [],
            'dose': null,
            'vaccinationSupplier': '0',
            'vaccinationCentre': '1',
            'paymentSummaryId': null,
            'paymentPrompt': {
                'paymentStatusTitle': '',
                'paymentStatusDetails': '',
                'line2': '',
                'line3': ''
            },
            'selectAllEmployeeBool': false,
            'bulkDoseDate': null,
            'bulkDose': '1',
            'employeeType': '1',
            'columnsCount': 1,
            'isTableLoading': true,
            'filterName': '',
            'filterIdentityType': 0,
            'filterIdentityNumber': '',
            'filterTier': '',
            'pagination': {
                'currentPage': 1,
                'totalPages': 0,
                'pageSize': 5,
                'scrollPosition': 0
            },
            'registeredFields': {
                'userId': null,
                'name': null,
                'phone': null,
                'email': null,
                'identityCardNumber': null,
                'dob': null,
                'gender': null,
                'nationality': null,
                'mySejahteraId': null
            },
            'unregisteredFields': {
                'name': null,
                'phone': null,
                'email': null,
                'identityCardNumber': null,
                'dob': null,
                'gender': '0',
                'nationality': null,
                'mySejahteraId': null
            },
            'employeeList': [],
            'swapConfirmation': false,
            'pendingSwapIndex': 0,
            'swapError': false,
            'swapMessage': null,
            'paymentReceipts': {
                'issued': [],
                'received': []
            },
            'paymentSlips': {
                'issued': [],
                'received': []
            },
            'invoices': {
                'issued': [],
                'received': []
            }
        };
    },
    'watch': {
        vaccinationCentre (val) {
            let vaccinationCentre = this.vaccinationCentres.find(vc => vc.i === val);
            this.vaccination.a1 = vaccinationCentre.a1;
            this.vaccination.a2 = vaccinationCentre.a2;
            this.vaccination.c = vaccinationCentre.ac;
            this.vaccination.s = vaccinationCentre.as;
            this.vaccination.p = vaccinationCentre.ap;
        },
        currentPage () {
            this.$nextTick(() => {
                this.getEmployees();
            });
        }
    },
    mounted () {
        this.getVaccination();
        this.getVaccinationSuppliers();

        this.columnsCount = this.$refs.listTable.rows[0].cells.length;
        this.getEmployees();

        if (this.$route.query && this.$route.query.status) {
            if (this.$route.query.status === '1') {
                this.paymentPrompt.paymentStatusTitle = 'Payment Successful!';
                this.paymentPrompt.paymentStatusDetails = 'Thank you, your payment has been successfully completed!';
                this.paymentPrompt.line2 = 'You will receive an email from us confirming the date of vaccination, either dose 1 or dose 2, once vaccination centre finish setting the date of vaccination.';
                this.paymentPrompt.line3 = 'Vacinee will receive SMS on vaccination date, time and location.';
            } else if (this.$route.query.status === '6') {
                this.paymentPrompt.paymentStatusTitle = 'Payment Pending.';
                this.paymentPrompt.paymentStatusDetails = 'Thank you, your payment status is pending.';
            } else {
                this.paymentPrompt.paymentStatusTitle = 'Payment Unsuccessful';
                this.paymentPrompt.paymentStatusDetails = 'Sorry, your payment was unsuccessful. Please try again.';
            }

            this.$refs.popupPaymentStatus.show();
        }

        // custom error message for sms
        const validateDict = {
            'custom': {
                'phone': {
                    'required': 'The phone field is required, for receiving SMS about the date of vaccination.'
                }
            }
        };

        this.$validator.localize('en', validateDict);
    },
    'methods': {
        getVaccination () {
            this.axios.get('Vaccination', {
                'params': {
                    'vaccinationId': this.vaccinationId
                }
            }).then(({ data }) => {
                this.role = data.data.ro;
                this.paymentSummaryId = data.data.psi;
                this.vaccination = data.data;

                if (this.isUserBookedByCompany) {
                    this.vaccinationPatient = this.vaccination.vp.find((vp) => vp.ui === this.vaccination.aui);
                } else if (this.vaccination.ut === 'User') {
                    this.vaccinationPatient = this.vaccination.vp[0];
                }

                if (this.isAdmin && this.vaccination.st === 'Paid') {
                    const regionId = this.vaccination.ri ? this.vaccination.ri : 0;
                    this.getVaccinationCentres(regionId);
                }

                if (this.paymentSummaryId) {
                    this.getVaccinationPaymentReceipt(this.paymentSummaryId);
                    this.getVaccinationInvoices(this.paymentSummaryId);
                    this.getVaccinationPaymentSlips(this.paymentSummaryId);
                    document.body.scrollTop = 0; // For Safari
                    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
                }
            });
        },
        getVaccinationCentres (orderByRegionId) {
            this.axios.get('VaccinationCentre/GetByRegion', {
                'params': {
                    'regionId': 0,
                    'orderByRegionId': orderByRegionId
                }
            }).then(({ data }) => {
                this.vaccinationCentres = data.data.vaccinationCentres;
            });
        },
        getVaccinationSuppliers () {
            this.axios.get('Vaccination/GetSuppliers').then(({ data }) => {
                this.vaccinationSuppliers = data.data;
            });
        },
        backToList () {
            window.history.back();
        },
        pay () {
            this.$refs.insvPayment.submit();
        },
        dismissPaymentStatus () {
            this.$refs.popupPaymentStatus.hide();
        },
        updateVaccinationSupplier () {
            this.isUpdating = true;
            this.axios.put('Vaccination/UpdateSupplier', {
                'vi': this.vaccination.vi,
                'vsi': this.vaccinationSupplier
            }).then(() => {
                this.getVaccination();
                this.isUpdating = false;
                this.$refs.assignVaccinationSupplierPopup.hide();
            });
        },
        updateVaccinationCentre () {
            this.isUpdating = true;
            this.axios.put('Vaccination/UpdateVaccinationCentre', {
                'vi': this.vaccination.vi,
                'vci': this.vaccinationCentre
            })
                .then(() => {
                    this.getVaccination();
                    this.isUpdating = false;
                    this.$refs.assignVaccinationCentrePopup.hide();
                });
        },
        payVaccinationCentre () {
            this.isUpdating = true;
            this.axios.put(`Vaccination/PayVaccinationCentre?vaccinationId=${this.vaccination.vi}`).then(() => {
                this.getVaccination();
                this.isUpdating = false;
                this.$refs.confirmPartnerPaymentPopup.hide();
            });
        },
        async validateDoseDate (dose) {
            // Validate
            let result = await this.$validator.validate('dose' + dose);
            if (!result) {
                return;
            }
            this.dose = dose;
            this.$refs.updateDoseDatePopup.show();
        },
        async validateBulkDoseDate () {
            // Validate
            let result = await this.$validator.validate('bulkDoseDate');
            if (!result) {
                return;
            }
            this.updateBulkDoseDate();
        },
        updateDoseDate () {
            let url, payload;
            url = 'Vaccination/UpdateDoseDate';
            payload = {
                'vpdi': this.vaccination.vp[0].d.find(d => d.d === this.dose).di,
                'd': convertDateToIso8601(this.doses[this.dose])
            };
            this.isUpdating = true;
            this.axios.put(url, payload).then(() => {
                this.getVaccination();
                this.isUpdating = false;
                this.$refs.updateDoseDatePopup.hide();
            });
        },
        updateBulkDoseDate () {
            let url; let payload; let temp = [];

            url = 'Vaccination/UpdateBulkDosesDate';

            for (let vaccinationPatient of this.vaccination.vp) {
                if (vaccinationPatient.isSelected === true) {
                    let vaccinationPatientDose = vaccinationPatient.d.find(d => d.d === parseInt(this.bulkDose));
                    temp.push(vaccinationPatientDose.di);
                }
            }

            payload = {
                'vpdi': temp,
                'd': convertDateToIso8601(this.bulkDoseDate)
            };

            this.isUpdating = true;
            this.axios.put(url, payload).then(() => {
                this.getVaccination();
                this.isUpdating = false;
                this.bulkDoseDate = null;
                this.$refs.updateBulkDoseDatePopup.hide();
            });
        },
        selectAllEmployee () {
            if (this.selectAllEmployeeBool) {
                for (let employee of this.vaccination.vp) {
                    employee.isSelected = false;
                }
            } else {
                for (let employee of this.vaccination.vp) {
                    employee.isSelected = true;
                }
            }
        },
        getEmployees () {
            this.axios.post('employeeTier/GetEmployeeList', {
                'p': this.pagination.currentPage,
                'ps': this.pagination.pageSize,
                'fn': this.filterName,
                'it': this.filterIdentityType,
                'in': this.filterIdentityNumber,
                'ti': this.filterTier
            }).then(({ data }) => {
                this.isTableLoading = false;
                let pagedResult = data.data;

                this.pagination.currentPage = pagedResult.p;
                this.pagination.totalPages = pagedResult.c;

                let list = pagedResult.l;

                this.employeeList = list.map((employee) => ({
                    'userId': employee.ui,
                    'name': employee.fn ? employee.fn : null,
                    'nric': employee.in ? employee.in : null,
                    'phone': employee.pn ? employee.pn : null,
                    'email': employee.e ? employee.e : null,
                    'dob': employee.dob ? employee.dob : null,
                    'gender': employee.g ? employee.g : null,
                    'tierName': employee.tn,
                    'employeeTierId': employee.eti,
                    'spouseCount': employee.sc,
                    'dependentCount': employee.dc
                }));
            });
        },
        selectEmployee (employee) {
            if (!employee.name || !employee.nric || !employee.phone || !employee.email || !employee.dob || !employee.gender) {
                alert('User profile incomplete! This employee has some incomplete information, please ask employee to update their profile!');
                return 0;
            }

            this.registeredFields.userId = employee.userId;
            this.registeredFields.name = employee.name;
            this.registeredFields.phone = employee.phone;
            this.registeredFields.email = employee.email;
            this.registeredFields.identityCardNumber = employee.nric;
            this.registeredFields.dob = employee.dob;
            this.registeredFields.gender = employee.gender;

            this.$refs.nationality.focus();
        },
        checkIfSelected (userId) {
            if (this.vaccination.vp) {
                let employee = this.vaccination.vp.find((e) => e.ui === userId);
                if (employee != null) {
                    return true;
                }
            }
            return false;
        },
        async confirmSwap () {
            if (this.employeeType === '1') {
                if (!this.registeredFields.name || !this.registeredFields.phone || !this.registeredFields.email || !this.registeredFields.identityCardNumber || !this.registeredFields.dob || !this.registeredFields.gender) {
                    alert('Please select an employee first!');
                    return 0;
                }

                let mySejahteraIdResult = await this.$validator.validate('mySejahteraId');
                let nationalityResult = await this.$validator.validate('nationality');

                if (!mySejahteraIdResult && !nationalityResult) {
                    return;
                }
            } else {
                let result = await this.$validator.validateAll('unregisteredEmployee');

                if (!result) {
                    return;
                }
            }

            this.swapConfirmation = true;
        },
        async swap () {
            this.swapMessage = 'Updating, please wait.....';
            this.isUpdating = true;

            let payload;
            if (this.employeeType === '1') {
                payload = {
                    'vpi': this.vaccination.vp[this.pendingSwapIndex].pi,
                    'ui': this.registeredFields.userId,
                    'na': this.registeredFields.nationality,
                    'msi': this.registeredFields.mySejahteraId
                };
            } else {
                payload = {
                    'vpi': this.vaccination.vp[this.pendingSwapIndex].pi,
                    'na': this.unregisteredFields.nationality,
                    'msi': this.unregisteredFields.mySejahteraId,
                    'n': this.unregisteredFields.name,
                    'p': this.unregisteredFields.phone,
                    'e': this.unregisteredFields.email,
                    'icn': this.unregisteredFields.identityCardNumber,
                    'd': convertDateToIso8601(this.unregisteredFields.dob),
                    'g': this.unregisteredFields.gender
                };
            }

            this.axios.put('Vaccination/SwapEmployee', payload)
                .then(() => {
                    this.registeredFields.userId = null;
                    this.registeredFields.name = null;
                    this.registeredFields.phone = null;
                    this.registeredFields.email = null;
                    this.registeredFields.identityCardNumber = null;
                    this.registeredFields.dob = null;
                    this.registeredFields.gender = null;
                    this.registeredFields.nationality = null;
                    this.registeredFields.mySejahteraId = null;

                    this.unregisteredFields.name = null;
                    this.unregisteredFields.phone = null;
                    this.unregisteredFields.email = null;
                    this.unregisteredFields.identityCardNumber = null;
                    this.unregisteredFields.dob = null;
                    this.unregisteredFields.gender = 0;
                    this.unregisteredFields.nationality = null;
                    this.unregisteredFields.mySejahteraId = null;

                    this.swapMessage = null;
                    this.swapConfirmation = false;
                    this.isUpdating = false;
                    this.$refs.popUpSwapEmployee.hide();
                    this.getVaccination();
                })
                .catch((e) => {
                    this.isUpdating = false;
                    this.swapMessage = e.response.data.message;
                    this.swapError = true;
                });
        },
        getVaccinationPaymentReceipt (paymentSummaryId) {
            this.axios.get('PaymentReceipt/GetByPaymentSummaryId', {
                'params': {
                    'paymentSummaryId': paymentSummaryId
                }
            }).then(({ data }) => {
                this.paymentReceipts.issued = data.data.issued;
                this.paymentReceipts.received = data.data.received;
            });
        },
        getVaccinationInvoices (paymentSummaryId) {
            this.axios.get('Invoice/GetByPaymentSummaryId', {
                'params': {
                    'paymentSummaryId': paymentSummaryId
                }
            }).then(({ data }) => {
                this.invoices.issued = data.data.issued;
                this.invoices.received = data.data.received;
            });
        },
        getVaccinationPaymentSlips (paymentSummaryId) {
            this.axios.get('PaymentSlip/GetByPaymentSummaryId', {
                'params': {
                    'paymentSummaryId': paymentSummaryId
                }
            }).then(({ data }) => {
                this.paymentSlips.issued = data.data.issued;
                this.paymentSlips.received = data.data.received;
            });
        }
    }
};
</script>

<style scoped>
.custom-checkbox.custom-checkbox-red .custom-control-input ~ .custom-control-label::before {
  background-color: rgba(255, 182, 182, 0.514)!important;
}

.custom-checkbox.custom-checkbox-blue .custom-control-input ~ .custom-control-label::before {
  background-color: rgba(181, 215, 243, 0.514)!important;
}

.custom-checkbox.custom-checkbox-red .custom-control-input:checked ~ .custom-control-label::before {
  background-color: rgb(238, 0, 0)!important;
}

.custom-checkbox.custom-checkbox-blue .custom-control-input:checked ~ .custom-control-label::before {
  background-color: rgb(1, 58, 247)!important;
}

.custom-checkbox .custom-control-input:focus ~ .custom-control-label::before {
  box-shadow: 0 0 0 1px #fff, 0 0 0 0.2rem rgba(0, 0, 0, 0.25)
}

.custom-checkbox.custom-checkbox-red .custom-control-input:checked:focus ~ .custom-control-label::before {
  box-shadow: 0 0 0 1px #fff, 0 0 0 0.2rem rgba(255, 182, 182, 0.514)
}

</style>
